// ─────────────────────────────────────────────────────────────
// ZIFEK — Référentiel des Edge Functions du back-end.
// Utilisé par l'explorateur superadmin (lecture seule + export).
// La source de chaque fonction vit dans : supabase/functions/<slug>/index.ts
// ─────────────────────────────────────────────────────────────

export interface EdgeFunctionInfo {
  slug: string;
  name: string;
  description: string;
  verifyJwt: boolean;
  category: 'IA' | 'Paiement' | 'Notifications' | 'Fichiers' | 'Domaines' | 'Système';
}

export const EDGE_FUNCTIONS: EdgeFunctionInfo[] = [
  { slug: 'bi-ai', name: 'BI AI', description: 'Génération et analyse de tableaux de bord décisionnels.', verifyJwt: false, category: 'IA' },
  { slug: 'design-to-page', name: 'Design to Page', description: 'Transforme un visuel ou une description en page web.', verifyJwt: true, category: 'IA' },
  { slug: 'generate-app-structure', name: 'Generate App Structure', description: 'Génère la structure complète d\u2019une application.', verifyJwt: true, category: 'IA' },
  { slug: 'product-ai-fill', name: 'Product AI Fill', description: 'Remplit automatiquement les fiches produits (nom, SEO…) depuis les images.', verifyJwt: true, category: 'IA' },
  { slug: 'document-ai', name: 'Document AI', description: 'Analyse et rédige des documents (Word, PDF).', verifyJwt: true, category: 'IA' },
  { slug: 'mon-assistant', name: 'Mon Assistant', description: 'Assistant IA conversationnel du marchand.', verifyJwt: true, category: 'IA' },
  { slug: 'chat-live', name: 'Chat Live', description: 'Messagerie en direct entre le marchand et ses visiteurs.', verifyJwt: false, category: 'IA' },
  { slug: 'zifek-client-chat', name: 'Zifek Client Chat', description: 'Support conversationnel côté client.', verifyJwt: true, category: 'IA' },
  { slug: 'credit-debt-import', name: 'Credit Debt Import', description: 'Import de créances et de dettes clients.', verifyJwt: true, category: 'Système' },
  { slug: 'send-booking-notification', name: 'Send Booking Notification', description: 'Envoi des notifications de réservation.', verifyJwt: true, category: 'Notifications' },
  { slug: 'send-password-reset', name: 'Send Password Reset', description: 'Envoi des emails de réinitialisation de mot de passe.', verifyJwt: false, category: 'Notifications' },
  { slug: 'cloudflare-domain', name: 'Cloudflare Domain', description: 'Configuration des domaines personnalisés via Cloudflare.', verifyJwt: true, category: 'Domaines' },
  { slug: 'upload-file', name: 'Upload File', description: 'Téléversement de fichiers vers le stockage.', verifyJwt: true, category: 'Fichiers' },
  { slug: 'upload-to-seaweedfs', name: 'Upload to SeaweedFS', description: 'Téléversement de médias vers SeaweedFS.', verifyJwt: true, category: 'Fichiers' },
  { slug: 'list-seaweedfs-files', name: 'List SeaweedFS Files', description: 'Liste et suppression des fichiers SeaweedFS.', verifyJwt: true, category: 'Fichiers' },
  { slug: 'setup-storage-bucket', name: 'Setup Storage Bucket', description: 'Crée et configure les buckets de stockage.', verifyJwt: true, category: 'Fichiers' },
  { slug: 'fix-storage-policies', name: 'Fix Storage Policies', description: 'Répare les politiques de sécurité du stockage.', verifyJwt: true, category: 'Fichiers' },
  { slug: 'theme-visit-track', name: 'Theme Visit Track', description: 'Suivi statistique des visites de thèmes.', verifyJwt: false, category: 'Système' },
  { slug: 'db-export', name: 'DB Export', description: 'Exploration et export de la base de données (lecture seule).', verifyJwt: true, category: 'Système' },
  { slug: 'manage-account-types', name: 'Manage Account Types', description: 'Gestion des types de compte de la plateforme.', verifyJwt: true, category: 'Système' },
];

const CATEGORY_COLORS: Record<EdgeFunctionInfo['category'], string> = {
  IA: 'bg-accent-100 text-accent-900',
  Paiement: 'bg-primary-100 text-primary-900',
  Notifications: 'bg-secondary-100 text-secondary-900',
  Fichiers: 'bg-background-200/70 text-foreground-800',
  Domaines: 'bg-secondary-100 text-secondary-900',
  Système: 'bg-foreground-100 text-foreground-800',
};

export function functionCategoryClass(category: EdgeFunctionInfo['category']): string {
  return CATEGORY_COLORS[category] || 'bg-background-200/70 text-foreground-800';
}

/** Construit le manifeste exportable des Edge Functions (JSON lisible). */
export function buildFunctionsManifest(): string {
  const payload = {
    export: 'zifek-edge-functions',
    generated_at: new Date().toISOString(),
    source_folder: 'supabase/functions',
    functions: EDGE_FUNCTIONS.map((fn) => ({
      slug: fn.slug,
      name: fn.name,
      description: fn.description,
      verify_jwt: fn.verifyJwt,
      category: fn.category,
      source_path: `supabase/functions/${fn.slug}/index.ts`,
    })),
  };
  return JSON.stringify(payload, null, 2);
}

/** Script shell pour redéployer toutes les fonctions ailleurs. */
export function buildFunctionsDeployScript(): string {
  const lines = [
    '#!/usr/bin/env bash',
    '# ZIFEK — Redéploiement des Edge Functions',
    '# Prérequis : CLI Supabase installée et projet lié (supabase link).',
    'set -e',
    '',
    'mkdir -p supabase/functions',
    '',
  ];
  for (const fn of EDGE_FUNCTIONS) {
    lines.push(`# ${fn.name} — ${fn.description}`);
    lines.push(`supabase functions deploy ${fn.slug}${fn.verifyJwt ? '' : ' --no-verify-jwt'}`);
    lines.push('');
  }
  return lines.join('\n');
}